import { Refresh } from "@mui/icons-material"
import { Box, Button, Container, Grid, Typography } from "@mui/material"
import { DataGridPro, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro"
import { useAppDispatch } from "app/hooks"
import { qSnack } from "app/snackSlice"
import { dataGridDEde } from "constants/dataGridLocale"
import dayjs from "dayjs"
import { PackError } from "generated/graphql"
import { enqueueSnackbar } from "notistack"
import { packErrorResolve, packErrorsGet } from "queries/fetchPackError"
import React, { useCallback, useEffect, useMemo, useState } from "react"

const PackErrorPage: React.FC = () => {
  const [loading, setloading] = useState<boolean>(false)
  const [packErrorsData, setPackErrorsData] = useState<PackError[]>([])
  const dispatch = useAppDispatch()
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })
  const fetchPackErrorsData = useCallback(async () => {
    setloading(true)
    const res = await packErrorsGet()
    if (res?.packErrors) {
      enqueueSnackbar("Fehler geladen!", { variant: "success" })
      setPackErrorsData(res.packErrors)
    }
    setloading(false)
  }, [setPackErrorsData])

  useEffect(() => {
    fetchPackErrorsData()
  }, [fetchPackErrorsData])

  const setSolved = async (ids: number[]) => {
    const res = await packErrorResolve(ids)
    if (res?.packErrorResolve) {
      dispatch(qSnack({ msg: "Fehler gelöst", severity: "success" }))
      fetchPackErrorsData()
    } else {
      dispatch(qSnack({ msg: "Fehler beim Löschen", severity: "error" }))
    }
    setSelectionModel({ type: "include", ids: new Set() })
  }

  const colsPackageError: GridColDef<PackError>[] = [
    { field: "id", headerName: "ID" },
    {
      field: "detail",
      headerName: "Detail",
      width: 600,
      renderCell: (params) => (
        <Typography
          variant="body2"
          component="div"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "created",
      headerName: "Datum",
      width: 150,
      valueFormatter: (_, row) =>
        dayjs(row.created).format("DD.MM.YYYY HH:mm:ss"),
    },
    { field: "orderID", headerName: "Bestell ID" },
    { field: "customerID", headerName: "Kunde ID" },
    { field: "addressID", headerName: "Adresse ID" },
    { field: "packer", headerName: "Packer", width: 150 },
    {
      field: "action",
      headerName: "Aktion",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={() => {
            setSolved([params.row.id])
          }}
        >
          Problem gelöst
        </Button>
      ),
    },
  ]

  const selection: PackError[] = useMemo(() => {
    if (selectionModel.type === "include") {
      return packErrorsData.filter((item) => selectionModel.ids.has(item.id))
    } else if (selectionModel.type === "exclude") {
      return packErrorsData.filter((item) => !selectionModel.ids.has(item.id))
    }
    return []
  }, [selectionModel, packErrorsData])

  return (
    <Container maxWidth="xl">
      <Grid container>
        <Grid size={6}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            sx={{ textTransform: "none" }}
            onClick={() => {
              fetchPackErrorsData()
            }}
          >
            Aktualisieren
          </Button>
        </Grid>
        <Grid size={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              sx={{ textTransform: "none" }}
              onClick={() => {
                setSolved(selection.map((item) => item.id))
              }}
            >
              ({selection.length}) Lösen
            </Button>
          </Box>
        </Grid>
        <Grid size={12}>
          <Box sx={{ height: "80vh", width: "100%" }}>
            <DataGridPro
              rows={packErrorsData}
              getRowHeight={() => "auto"}
              showToolbar
              checkboxSelection
              rowSelectionModel={selectionModel}
              getRowId={(row) => row.id}
              onRowSelectionModelChange={(newSelection) => { setSelectionModel(newSelection) }}
              loading={loading}
              columns={colsPackageError}
              disableRowSelectionOnClick
              localeText={dataGridDEde}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default PackErrorPage
